import { useMemo, useRef } from "react";

import { DollarSign, PercentIcon } from "lucide-react";

import { DataChart } from "src/components/dataView/DataChart/DataChart";
import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import {
  colorGroupColumn,
  colorGroupLabelColumn,
  dateHeader,
  getExcelExportData,
  getRecentMonths,
  styledDateHeader,
  transposeDataToRows,
} from "src/components/dataView/DataTable/tableHelpers";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { normalizedPLAtom, useAnalytics } from "src/lib/hooks/useAnalytics";
import { NORMALIZED_PL_QUERY } from "src/lib/queries/analytics";
import { cn } from "src/lib/utils";

const createRowMapper = (numberFormat) => (columns, data) =>
  transposeDataToRows(
    columns,
    data,
    (d, col) => d.period === col,
    (map) => [
      {
        id: "NET REVENUE",
        label: "NET REVENUE",
        meta: { color: "var(--chart-1)" },
        subRows: [
          {
            label: "Gross Product Sales",
            ...map("grossSales"),
          },
          {
            label: "Less: Discounts",
            format: numberFormat.currencyBrackets,
            ...map("discounts"),
          },
          {
            label: "Less: Refunds",
            format: numberFormat.currencyBrackets,
            ...map("refunds"),
          },
          {
            label: "Add: Shipping Collected",
            ...map("shipping"),
          },
          {
            label: "TOTAL NET REVENUE",
            meta: {
              bold: true,
            },
            ...map("netRevenue"),
          },
        ],
        ...map("netRevenue"),
      },
      {
        label: "GROWTH RATE",
        format: numberFormat.percentWithSign,
        icon: PercentIcon,
        meta: { highlight: true },
        ...map((rowData) => {
          const prevRow = data.find(
            (d) => d.period === columns[columns.indexOf(rowData.period) - 1]
          );
          if (!prevRow || !rowData.netRevenue || !prevRow.netRevenue) {
            return 0;
          }
          return (
            ((rowData.netRevenue - prevRow.netRevenue) / prevRow.netRevenue) *
            100
          );
        }),
      },
      {
        label: "VARIABLE COSTS",
        meta: { color: "var(--chart-2)" },
        subRows: [
          {
            label: "COGS",
            ...map("cogs"),
          },
          {
            label: "Carrier and Fulfillment Fees",
            ...map("shippingCost"),
          },
          {
            label: "Payment Processing Fees",
            ...map("transactionFee"),
          },
        ],
        ...map((rowData) => {
          return rowData.cogs + rowData.shippingCost + rowData.transactionFee;
        }),
      },
      {
        label: "GROSS PROFIT",
        icon: DollarSign,
        meta: { highlight: true },
        ...map("grossProfit"),
      },
      {
        label: "GROSS MARGIN",
        format: numberFormat.percent,
        icon: PercentIcon,
        meta: { highlight: true },
        ...map((rowData) => {
          return (rowData.grossProfit / (rowData.netRevenue || 1)) * 100 || 0;
        }),
      },
      {
        label: "MARKETING COSTS",
        meta: { color: "var(--chart-3)" },
        subRows: [
          {
            label: "Paid Ads",
            meta: { color: "var(--chart-3)", showExpandedValue: true },
            subRows: [
              { label: "Meta", ...map("metaMarketingSpend") },
              { label: "Google", ...map("googleMarketingSpend") },
            ],
            ...map("marketingSpend"),
          },
          {
            label: "Other Direct Marketing",
            ...map("otherMarketingSpend"),
          },
        ],
        ...map(
          (rowData) => rowData.marketingSpend + rowData.otherMarketingSpend
        ),
      },
      {
        label: "PROFIT CONTRIBUTION",
        icon: DollarSign,
        meta: { highlight: true },
        ...map("profitContribution"),
      },
      {
        label: "CONTRIBUTION MARGIN",
        format: numberFormat.percent,
        icon: PercentIcon,
        meta: { highlight: true },
        ...map((rowData) => {
          return (
            (rowData.profitContribution / (rowData.netRevenue || 1)) * 100 || 0
          );
        }),
      },
      {
        label: "FIXED COSTS",
        meta: { color: "var(--chart-4)" },
        subRows: [
          {
            label: "Operating Expenses",
            ...map("operatingExpense"),
          },
        ],
        ...map("operatingExpense"),
      },
      {
        label: "NET PROFIT",
        icon: DollarSign,
        meta: { highlight: true },
        tooltip: "Profit before interest, depreciation & amortization, taxes",
        ...map("netIncome"),
      },
      {
        label: "NET MARGIN",
        format: numberFormat.percent,
        icon: PercentIcon,
        meta: { highlight: true },
        ...map((rowData) => {
          return (rowData.netIncome / (rowData.netRevenue || 1)) * 100 || 0;
        }),
      },
    ]
  );

const chartConfig = {
  netRevenue: {
    color: "var(--chart-1)",
    label: "Net Revenue",
  },
  variableCosts: {
    color: "var(--chart-2)",
    label: "Variable Costs",
  },
  marketingCosts: {
    color: "var(--chart-3)",
    label: "Marketing Costs",
  },
  fixedCosts: {
    color: "var(--chart-4)",
    label: "Fixed Costs",
  },
};

const excelStyling = [
  {
    rows: [1],
    bold: true,
    border: "bottom",
  },
  {
    rows: [7],
    bold: true,
  },
  {
    rows: [8, 13, 14, 20, 21, 24, 25],
    cellColor: "E3DFEB",
  },
  {
    rows: [8, 14, 21],
    border: "bottom",
  },
  {
    columns: ["A"],
    border: "right",
  },
];

const NormalizedPL = () => {
  const analytics = useAnalytics(
    normalizedPLAtom,
    NORMALIZED_PL_QUERY,
    "normalizedPL",
    ({ data, periods: _periods, dateMin }) => {
      const periods = (_periods || []).sort((a, b) => a.localeCompare(b));

      // fill in any missing months past the min date
      const missingData = periods.reduce((acc, curr) => {
        if (new Date(curr) >= dateMin && !data.find((d) => d.period === curr)) {
          acc.push({
            period: curr,
          });
        }
        return acc;
      }, []);

      return [...data, ...missingData].sort((a, b) =>
        a.period.localeCompare(b.period)
      );
    }
  );
  const { store, refreshData, selectedIntegration, numberFormat } = analytics;
  const mapToRows = useMemo(
    () => createRowMapper(numberFormat),
    [numberFormat]
  );
  const { resolution = "month" } = store;

  const chartData = useMemo(
    () =>
      store.data.map((row) => ({
        period: dateHeader(
          row.period,
          resolution,
          store.dateFrom,
          store.dateTo
        ),
        netRevenue: row.netRevenue,
        variableCosts: row.cogs + row.shippingCost + row.transactionFee,
        marketingCosts: row.marketingSpend + row.otherMarketingSpend,
        fixedCosts: row.operatingExpense,
        netIncome: row.netIncome,
      })),
    [resolution, store.data, store.dateFrom, store.dateTo]
  );

  const columns = useMemo(() => {
    const { periods, loading } = store;
    const activePeriods = (periods || getRecentMonths()).sort((a, b) =>
      a.localeCompare(b)
    );

    return [
      colorGroupColumn,
      colorGroupLabelColumn,
      ...activePeriods.map((p) => ({
        id: p + (loading ? "-loading" : ""),
        header: styledDateHeader(p, resolution, store.dateFrom, store.dateTo),
        meta: { className: "min-w-[100px]" },
        accessorKey: p,
        cell: ({ getValue, row }) => {
          // hide value if parent row and is expanded
          if (
            row.getCanExpand() &&
            row.getIsExpanded() &&
            !row.original.meta?.showExpandedValue
          ) {
            return null;
          }

          const format = row.original.format || numberFormat.currency;
          const wrapper = (value) => (
            <div
              className={cn(
                "text-right",
                row.original?.meta?.bold && "font-bold"
              )}
            >
              {format(value)}
            </div>
          );

          // TODO: this is just for demo purposes, remove when real data is available
          // return stackedValue(wrapper(getValue()), wrapper(getValue() / 2));
          return wrapper(getValue());
        },
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.periods, store.loading, numberFormat]);

  const loadingData = useMemo(() => {
    // return some mock data while loading
    return mapToRows(
      getRecentMonths(),
      Array.from({ length: 12 }, (_, i) => ({
        period: new Date(Date.now() - i * 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 7),
      }))
    );
  }, [mapToRows]);

  const [tableData, exportTableData, tableHeaders] = useMemo(() => {
    // Prepare table headers for DataTable export
    // Skip the first column as it is a group color column
    const tableHeaders = columns.slice(1).map(({ header, accessorKey }) => ({
      header,
      accessorKey,
    }));

    let tableData;
    let exportTableData;
    if (store.data.length) {
      // transpose the data to match the expected format
      tableData = mapToRows(store.periods, store.data);

      // prepare export data
      exportTableData = getExcelExportData(
        tableData,
        tableHeaders,
        "label",
        numberFormat.currency
      );
    }

    return [tableData, exportTableData, tableHeaders];
  }, [columns, store.data, store.periods, mapToRows, numberFormat]);

  // memoized because some little shit is misbehaving and re-renders this component on data load
  const initialState = useMemo(() => {
    return {
      expanded: {
        "NET REVENUE": true,
      },
    };
  }, []);

  const imageExportRef = useRef(null);

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters className="justify-between">
          <DateRangePicker
            useReportingInterval
            atom={normalizedPLAtom}
            onSubmit={refreshData}
          />

          <DataExportButton
            disabled={store.loading}
            title="Profit & Loss"
            integrationName={selectedIntegration?.description}
            dateFrom={store.dateFrom}
            dateTo={store.dateTo}
            tableData={exportTableData}
            tableHeaders={tableHeaders}
            imageExportRef={imageExportRef}
            styles={excelStyling}
          />
        </DashboardLayout.Filters>

        <DataChart
          data={chartData}
          loading={store.loading}
          dateFrom={store.dateFrom}
          dateTo={store.dateTo}
          xAxisDataKey="period"
          chartConfig={chartConfig}
          title="Profit & Loss"
          showYAxis
          imageExportRef={imageExportRef}
        />

        <DataTable
          idField="label"
          columns={columns}
          data={tableData}
          atom={normalizedPLAtom}
          className="max-w-fit"
          loadingData={loadingData}
          initialState={initialState}
        />
      </DashboardLayout.Main>
    </>
  );
};

export default NormalizedPL;
